<!--
  @honest-pitches/toast — ToastViewport.vue

  Renders the global toast queue from the framework-agnostic core.
  Mount it ONCE near the app root:

    <ToastViewport />

  Tone rules (per `index.ts#effectiveDismissMs`):
    - success → green, auto-dismiss 3s
    - info    → neutral, auto-dismiss 5s
    - warning → amber, auto-dismiss 5s
    - error   → red, sticky until manually dismissed

  ARIA: success/info/warning use `role="status"` +
  `aria-live="polite"` (non-urgent). Errors use `role="alert"`
  + `aria-live="assertive"` so screen readers interrupt the
  current utterance. The 2026-07-18 contract: errors MUST be
  sticky + assertive, never auto-dismiss silently — the operator
  complained (2026-07-18) that the Studio's prior
  flashToast was hiding failures in a transient green box that
  vanished before the operator could read it.
-->
<template>
  <Teleport to="body">
    <TransitionGroup
      tag="div"
      class="hp-toast-viewport"
      :class="[`hp-toast-viewport--${placement}`]"
      name="hp-toast"
      data-testid="hp-toast-viewport"
      aria-live="polite"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="['hp-toast', `hp-toast--${toast.tone}`]"
        :role="toast.tone === 'error' ? 'alert' : 'status'"
        :aria-live="toast.tone === 'error' ? 'assertive' : 'polite'"
        :data-testid="`hp-toast`"
        :data-toast-tone="toast.tone"
        :data-toast-id="toast.id"
      >
        <div class="hp-toast__body">
          <p class="hp-toast__message">{{ toast.message }}</p>
          <p v-if="toast.detail" class="hp-toast__detail">{{ toast.detail }}</p>
        </div>
        <button
          type="button"
          class="hp-toast__dismiss"
          :aria-label="`Dismiss ${toast.tone} notification`"
          :data-testid="`hp-toast-dismiss`"
          @click="dismiss(toast.id)"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script setup lang="ts">
import { useToast } from "./index.js";

withDefaults(
  defineProps<{
    /**
     * Where to anchor the toast stack.
     *   - `top-right` (default) — desktop convention
     *   - `top-center` — mobile / overlay modal contexts
     *   - `bottom-right` — chat-style overlays (future)
     */
    placement?: "top-right" | "top-center" | "bottom-right";
  }>(),
  { placement: "top-right" },
);

const { toasts, dismiss } = useToast();
</script>

<style src="./style.css"></style>
